import React, { useRef, useState } from 'react';
import useBusinessBranding from '../features/businesses/hooks/useBusinessBranding';
import { formatApiError } from '../utils/errorMessage';
import { assetUrl } from '../utils/assetUrl';

const MAX_FILE_SIZE_MB = 5;

const dispatchBrandingUpdate = (businessId) => {
  window.dispatchEvent(new CustomEvent('business-branding-updated', {
    detail: { businessId }
  }));
};

export default function BusinessLogoManager({ businessId, onChange }) {
  const fileInputRef = useRef(null);
  const { branding, uploadLogo, removeLogo, loading } = useBusinessBranding(businessId, { enabled: !!businessId });
  const [error, setError] = useState('');
  const [previewUrl, setPreviewUrl] = useState(null);
  const [brokenLogo, setBrokenLogo] = useState(null);

  const savedLogo = assetUrl(branding?.logo_url);
  // A saved logo whose image can't be loaded shows the "No Logo" placeholder.
  const logoMissing = !previewUrl && (Boolean(branding?.logo_missing) || (Boolean(savedLogo) && savedLogo === brokenLogo));
  const currentLogo = previewUrl || (savedLogo && savedLogo !== brokenLogo ? savedLogo : null);

  const handleFileSelect = async (event) => {
    const file = event.target.files?.[0];
    if (!file) return;
    if (file.size > MAX_FILE_SIZE_MB * 1024 * 1024) {
      setError(`Logo must be smaller than ${MAX_FILE_SIZE_MB}MB.`);
      return;
    }
    setError('');
    try {
      setPreviewUrl(URL.createObjectURL(file));
      await uploadLogo(file);
      dispatchBrandingUpdate(businessId);
      if (onChange) onChange();
    } catch (err) {
      setError(formatApiError(err, 'Failed to upload logo'));
      setPreviewUrl(null);
    } finally {
      if (fileInputRef.current) fileInputRef.current.value = '';
    }
  };

  const handleRemove = async () => {
    try {
      await removeLogo();
      setPreviewUrl(null);
      dispatchBrandingUpdate(businessId);
      if (onChange) onChange();
    } catch (err) {
      setError(formatApiError(err, 'Failed to remove logo'));
    }
  };

  return (
    <div className="border border-gray-200 rounded-lg p-4 space-y-3">
      <div className="flex items-center gap-4">
        <div className="h-16 w-16 rounded-md border border-gray-200 bg-gray-50 flex items-center justify-center overflow-hidden">
          {currentLogo ? (
            <img
              src={currentLogo}
              alt="Business Logo"
              className="object-contain h-full w-full"
              onError={() => { if (!previewUrl) setBrokenLogo(savedLogo); }}
            />
          ) : (
            <span className="text-xs text-gray-500 text-center px-1">No Logo</span>
          )}
        </div>
      {logoMissing && (
        <p role="alert" className="text-sm text-amber-700">Logo missing, please re-upload.</p>
      )}
        <div>
          <p className="text-sm text-gray-600">
            Upload a square logo (PNG/JPG/WebP, max {MAX_FILE_SIZE_MB}MB). It will appear in the header and on quote and invoice PDFs.
          </p>
          {error && <p className="text-xs text-red-600 mt-1" role="alert">{error}</p>}
        </div>
      </div>
      <div className="flex gap-3">
        <button
          type="button"
          onClick={() => fileInputRef.current?.click()}
          className="bg-blue-600 text-white px-3 py-2 rounded hover:bg-blue-700 text-sm disabled:opacity-60"
          disabled={!businessId || loading}
        >
          {currentLogo ? 'Replace Logo' : 'Upload Logo'}
        </button>
        {currentLogo && (
          <button
            type="button"
            onClick={handleRemove}
            className="border border-gray-300 px-3 py-2 rounded text-sm hover:bg-gray-50 disabled:opacity-60"
            disabled={loading}
          >
            Remove
          </button>
        )}
        <input
          ref={fileInputRef}
          type="file"
          accept="image/png,image/jpeg,image/webp,image/svg+xml"
          className="hidden"
          onChange={handleFileSelect}
        />
      </div>
    </div>
  );
}

